import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OrganicShell from '../components/organic/OrganicShell';
import ScoringTab from '../components/settings/ScoringTab';
import TerritoriesTab from '../components/settings/TerritoriesTab';
import OrganisationsTab from '../components/settings/OrganisationsTab';
import ResearchChangesTab from '../components/settings/ResearchChangesTab';
import HealthTab from '../components/settings/HealthTab';

// Settings — one home for the admin tabs that were cluttering the
// navbar: Scoring and Territories for any org admin, plus Organisations
// and Data health for super admins only. The super-admin tabs are not
// rendered at all for other users — no padlock, no disabled state; the
// backend guards those endpoints as the fallback. Billing is just a link
// out to the Stripe customer portal, here and in the rail's account menu.

export const STRIPE_PORTAL_URL = 'https://billing.stripe.com/p/login/6oUbJ18I46yz6oy5pu7g400';

// Tabs any org admin gets. Scoring stays the landing tab — it is the
// one every tenant has something to say about.
const ORG_TABS = [
  { key: 'scoring', label: 'Scoring' },
  { key: 'territories', label: 'Territories' },
];

const SUPER_TABS = [
  { key: 'organisations', label: 'Organisations' },
  { key: 'health', label: 'Data health' },
  { key: 'research-changes', label: 'Research changes' },
];
const SUPER_ONLY = new Set(SUPER_TABS.map((t) => t.key));

function Settings() {
  const { isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuper = isSuperAdmin();

  // A non-super admin can never land on a super-admin-only tab.
  const requested = searchParams.get('tab') || 'scoring';
  const known = ORG_TABS.some((t) => t.key === requested)
    || SUPER_TABS.some((t) => t.key === requested);
  const tab = !known || (SUPER_ONLY.has(requested) && !isSuper) ? 'scoring' : requested;

  const selectTab = (key) => {
    setSearchParams(key === 'scoring' ? {} : { tab: key }, { replace: true });
  };

  const tabButton = ({ key, label }) => (
    <button
      key={key}
      type="button"
      className={tab === key ? 'btn btn-primary' : 'btn btn-secondary'}
      style={{ fontSize: 13 }}
      aria-current={tab === key ? 'page' : undefined}
      onClick={() => selectTab(key)}
    >
      {label}
    </button>
  );

  return (
    <OrganicShell active="settings">
      <header>
        <h1 className="o-page-title">Settings</h1>
        <p className="o-page-subhead">
          {isSuper
            ? 'Scoring, territories and billing for your organisation, plus the platform-wide sections you manage as a super admin.'
            : 'Scoring, territories and billing for your organisation.'}
        </p>
      </header>

      <div className="o-tab-row">
        {ORG_TABS.map(tabButton)}
        <a
          className="btn btn-secondary"
          style={{ fontSize: 13 }}
          href={STRIPE_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Billing <ExternalLink size={13} strokeWidth={2.75} />
        </a>
        {isSuper && (
          <>
            <span className="o-tab-divider" aria-hidden="true" />
            {SUPER_TABS.map(tabButton)}
          </>
        )}
      </div>

      {tab === 'scoring' && <ScoringTab />}
      {tab === 'territories' && <TerritoriesTab />}
      {tab === 'organisations' && isSuper && <OrganisationsTab />}
      {tab === 'health' && isSuper && <HealthTab />}
      {tab === 'research-changes' && isSuper && <ResearchChangesTab />}
    </OrganicShell>
  );
}

export default Settings;

